import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Instagram, Linkedin, Heart, ChevronRight, Phone, Mail, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    'Our Services': [
      { name: 'Web Development', path: '/services/website-development' },
      { name: 'Mobile Apps', path: '/services/android-development' },
      { name: 'Cloud Solutions', path: '/services/cloud-solutions' },
      { name: 'E-Commerce', path: '/services/ecommerce' },
      { name: 'UI/UX Design', path: '/services/ui-ux-design' },
    ],
    'Quick Links': [
      { name: 'About Us', path: '/about' },
      { name: 'Products', path: '/products' },
      { name: 'Contact Us', path: '/contact' },
      { name: 'Privacy Policy', path: '/privacy' },
      { name: 'Terms of Service', path: '/terms' },
    ]
  };

  return (
    <footer className="relative bg-white text-slate-900 pt-10 md:pt-16 pb-6 md:pb-8 overflow-hidden border-t border-slate-100">
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        {/* Top Grid: Brand & Links */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-12 mb-8 md:mb-16">
          {/* Brand Section */}
          <div className="col-span-2 space-y-4 md:space-y-6">
            <Link to="/" className="flex items-center gap-2 group">
              <img src="/images/logo-meharetech.png" alt="Logo" className="w-10 h-10 object-contain" />
              <span className="text-xl md:text-2xl font-black tracking-tighter text-slate-900">Mehare<span className="text-primary">Tech</span></span>
            </Link>
            <p className="text-slate-500 text-xs md:text-sm leading-relaxed max-w-md font-medium">
              We empower visionaries with cutting-edge technology. From startups to enterprises, we build the digital infrastructure of tomorrow.
            </p>
          </div>

          {/* Link Sections */}
          {Object.entries(footerLinks).map(([title, links]) => (
            <div key={title} className="space-y-4 md:space-y-6">
              <h4 className="text-base md:text-lg font-bold text-slate-900 tracking-tight">{title}</h4>
              <ul className="space-y-2 md:space-y-3">
                {links.map((link) => (
                  <li key={link.name}>
                    <Link to={link.path} className="text-slate-500 hover:text-primary transition-colors flex items-center gap-2 text-xs md:text-sm group font-medium">
                      <ChevronRight size={12} className="text-primary group-hover:translate-x-1 transition-transform" />
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Section: Contact & Map */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center pb-8 md:pb-12">
          {/* Get In Touch */}
          <div className="space-y-8">
            <div>
              <h4 className="text-xl md:text-2xl font-black mb-4 md:mb-6 text-slate-900">Get In Touch</h4>
              <div className="space-y-2 md:space-y-4">
                <a href="tel:+918708604830" className="flex items-center gap-3 md:gap-4 text-slate-600 hover:text-primary transition-all group">
                  <div className="w-8 h-8 md:w-10 md:h-10 bg-slate-50 rounded-lg md:rounded-xl flex items-center justify-center border border-slate-100 group-hover:bg-primary group-hover:text-white transition-all shadow-sm">
                    <Phone size={14} className="md:size-[18px]" />
                  </div>
                  <span className="text-xs md:text-sm font-semibold tracking-tight">+91 87086 04830</span>
                </a>
                <a href="mailto:info@meharetech.com" className="flex items-center gap-3 md:gap-4 text-slate-600 hover:text-primary transition-all group">
                  <div className="w-8 h-8 md:w-10 md:h-10 bg-slate-50 rounded-lg md:rounded-xl flex items-center justify-center border border-slate-100 group-hover:bg-primary group-hover:text-white transition-all shadow-sm">
                    <Mail size={14} className="md:size-[18px]" />
                  </div>
                  <span className="text-xs md:text-sm font-semibold tracking-tight">info@meharetech.com</span>
                </a>
                <div className="flex items-center gap-3 md:gap-4 text-slate-600 group">
                  <div className="w-8 h-8 md:w-10 md:h-10 bg-slate-50 rounded-lg md:rounded-xl flex items-center justify-center border border-slate-100 shadow-sm">
                    <MapPin size={14} className="md:size-[18px]" />
                  </div>
                  <span className="text-xs md:text-sm font-semibold tracking-tight">Sundar Nagar, Hisar, Haryana</span>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex gap-3 md:gap-4">
              {[
                { Icon: Facebook, url: "#" },
                { Icon: Twitter, url: "#" },
                { Icon: Instagram, url: "https://www.instagram.com/meharetech_/" },
                { Icon: Linkedin, url: "#" }
              ].map((social, idx) => (
                <motion.a
                  key={idx}
                  whileHover={{ y: -2, scale: 1.05 }}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 md:w-11 md:h-11 bg-white rounded-lg md:rounded-xl flex items-center justify-center hover:bg-primary hover:text-white text-slate-600 transition-all border border-slate-100 shadow-xl shadow-slate-200/50"
                >
                  <social.Icon size={16} className="md:size-[18px]" />
                </motion.a>
              ))}
            </div>
          </div>

          {/* Integrated Map */}
          <div className="relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-primary/10 to-purple-600/10 rounded-2xl md:rounded-3xl blur opacity-75 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>
            <div className="relative overflow-hidden rounded-xl md:rounded-2xl border border-slate-100 bg-white shadow-2xl shadow-slate-200/50">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d258.9207920162957!2d75.72186298628542!3d29.176190683615637!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x391232c90939ee57%3A0xedc79e09ab19ce12!2s420%2C%20Sundar%20Nagar%2C%20Hisar%2C%20Haryana%20125001!5e0!3m2!1sen!2sin!4v1770665724971!5m2!1sen!2sin"
                width="100%"
                height="180"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full grayscale h-full active:grayscale-0 transition-all duration-500"
                title="MehareTech HQ Location"
              ></iframe>
              <div className="p-3 bg-white/90 backdrop-blur-md flex justify-between items-center border-t border-slate-100">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Our HQ Location</span>
                <a
                  href="https://maps.app.goo.gl/RWsEGPx2DMc7cisG8"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary text-[10px] font-black hover:underline"
                >
                  VIEW LARGER MAP
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-6 md:pt-8 border-t border-slate-100 flex flex-col md:flex-row justify-between items-center gap-4 md:gap-6 text-center md:text-left">
          <p className="text-slate-400 text-[10px] md:text-xs font-semibold tracking-wide lowercase">
            &copy; 2012-{currentYear} MEHARETECH SOLUTIONS. ALL RIGHTS RESERVED.
          </p>
          <div className="flex items-center gap-2 text-slate-400 text-[10px] md:text-xs font-semibold lowercase">
            architected by <span className="text-slate-900">meharetech</span> with <Heart size={12} className="text-red-500 animate-pulse" />
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
